/**
 * Every derivation the board needs to paint targeting: the single overlay-mode vocabulary, the
 * movement/sensor/attack washes, the AoE hover templates, the un-targetable token set, and the
 * per-cell sightline advisory. They belong together because they are ONE exclusivity contract —
 * each memo ANDs its gate on `overlayMode` so exactly one dot vocabulary ever renders — and because
 * they share the same three inputs (live `state`, the single armed `aim`, the view mode).
 *
 * Every rule here is the UI twin of a server wall (`reachableCells`, `movePathPreview`,
 * `attackReachCellList`, `legalDirectedDeployCells`, `ramTargetReason`, `techTargetReason`, …) —
 * imported, never re-derived, so an affordance cannot drift from the authority that enforces it.
 */
import { useCallback, useMemo, useRef } from "react";
import {
  controlsCombatant,
  reachableCells,
  movePathPreview,
  trampleTrailTo,
  hasStatus,
  worldFor,
  isActor,
  sideOf,
  TRAMPLING_STATUS,
  weaponInstanceKey,
  type Cell,
  type EncounterState,
  type ProjectedEncounterState,
  type MovePathPreview,
  type SquareCell,
} from "@lancer-vtt/domain";
import { forecastMove, legalDirectedDeployCells, type LoadedOverlay, type MoveForecast } from "@lancer-vtt/rules";
import type { useEncounter } from "../../../lib/encounter/useEncounter";
import { planningStateFor } from "../../../lib/encounter/activationPreview";
import { boostRingCells, moveVerbReach, type MovePreview } from "../../../lib/encounter/boostRing";
import { washAimFor } from "../../../lib/encounter/fireSequence";
import { moveForecastFor } from "../../../lib/encounter/disclosure";
import { hoverIntelFor, type ThreatRing, type ThreatRingsView } from "../../../lib/encounter/hoverIntel";
import type { DragRings } from "../combatStage/props";
import type { TrampleTrail } from "../combatStage/props";
import { overlayModeFor, reachableCellKeys, LOS_VEIL_EXCLUDED_AIM_MODES, type OverlayMode } from "../combatCells";
import {
  armedCellSightlines,
  ramTargetReason,
  grappleTargetReason,
  improvisedAttackTargetReason,
  meleeReachForAim,
  searchTargetReason,
  mountTargetReason,
  jockeyTargetReason,
  rideTargetReason,
  sensorReachCells,
  techTargetReason,
  directedDeployPendingMarks,
  type ActionDraft,
  type CellSightline,
} from "../../../lib/encounter/combatInspector";
import {
  attackReachCellList,
  attackAffectedKeys,
  attackAffectedCombatantKeys,
  combatantFootprintKeys,
  combatantInReachKeys,
  isAreaRange,
  legalPilotBridgeCells,
  legalRideDismountCells,
  weaponProfileForAim,
  weaponRangeForAim,
  weaponTargetBlocked,
  activateReachForAim,
} from "../../../lib/encounter/attackRange";

type EncounterRoom = ReturnType<typeof useEncounter>;

export function useTargetingWashes({
  state,
  aim,
  selectedId,
  viewMode,
  directionPromptActive,
  prompt,
  actorId,
  isGm,
  previewAim,
  previewMove,
  hoverUnitId,
  intelOff = false,
  overlay,
}: {
  state: EncounterRoom["state"];
  aim: ActionDraft | null;
  selectedId: string | null;
  viewMode: "edit" | "play";
  directionPromptActive: boolean;
  prompt: EncounterRoom["prompt"];
  /**
   * The viewer's own slot id — the same identity the room attributes an intent to. Used by the
   * movement affordances below through {@link planningStateFor} (`moveStateFor`), whose first step
   * is `previewIfControlled` (2.4): the room folds an `activate_unit` in front of a controlled,
   * `"ready"` unit's `request_move` and resolves the move against that preview, so the wash and the
   * drag trail read the same preview or the board paints nothing for a move the server would accept.
   * It also feeds the boost ring's own `previewIfControlled` read.
   */
  actorId: string | null | undefined;
  /**
   * The viewer is the GM (`session.role === "gm"`). Read only by {@link planningStateFor}: the GM
   * may plan any unit off-turn (the `canControl` rule), because a planning state commits nothing.
   */
  isGm: boolean;
  /**
   * The command menu's HOVER preview (`projects/token-command-menu.md` 2.4): the aim a hovered row
   * would arm. While no aim is armed it stands in for `aim` in every WASH derivation below (the
   * overlay mode, the reach washes, the sightlines, the target set), so a hovered weapon paints the
   * exact reach an armed one would. It never reaches the click-routing flags (`attackArea`,
   * `cellPickArmed`, `targetPicking`, the AoE hover templates): a preview is not an aim.
   */
  previewAim?: ActionDraft | null;
  /**
   * The command menu's MOVEMENT hover preview (Phase 3): a hovered Boost or Disengage row. While no
   * aim is armed or previewed, the movement wash paints the cells that unit could reach after the
   * move (`moveVerbReach`, over its `moveStateFor` state) and the boost ring stands down.
   */
  previewMove?: MovePreview | null;
  /**
   * The unit under the pointer (`projects/hover-intel.md`), already resolved and
   * suppressed by the caller under a hovered menu row, a drag, touch input and EDIT. It paints
   * {@link hoverIntelFor}'s zones in the `"intel"` overlay mode, cut to what this viewer knows.
   */
  hoverUnitId?: string | null;
  /**
   * Hover intel is off for this viewer right now (`useHoverIntelUnit`'s `off`: an armed aim, a
   * hovered menu row, a direction prompt, EDIT, touch). While it is on, the selected unit keeps its
   * rings beside its movement wash (`projects/intel-follows-selection.md`) and a drag's rings follow
   * the drop cell ({@link threatRingsAt}).
   */
  intelOff?: boolean;
  /** The merged overlay, so the attack sightline hatch counts allied hard cover (the Goliath's Guardian) — see `armedCellSightlines`. */
  overlay?: LoadedOverlay;
}) {
  // What the washes read: the armed aim, else the hovered preview (a hovered weapon of the armed
  // shot's fire sequence shows its own range, `washAimFor`).
  const washAim = washAimFor(aim, previewAim ?? null);
  const moveHover = washAim === null && !directionPromptActive && viewMode === "play" ? (previewMove ?? null) : null;
  // The board's single active dot vocabulary — a
  // pure function of the board-wide armed `aim`, the current selection, `viewMode`, and
  // whether a pending declared-phase direction prompt is
  // active, extracted to `combatCells.ts` (`overlayModeFor`) so it unit-tests without the
  // component. Each overlay memo below ANDs its existing gate on this so exactly one vocabulary
  // ever renders — the load-bearing fix: `reachable` used to gate only on `viewMode === "play" &&
  // selectedId`, never on `aim`, which is why the move-dot wash and the red attack-range wash
  // could stack. 3.3 widened the precedence so a direction prompt reads as `"movement"`
  // unconditionally (see `overlayModeFor`'s own doc for why it outranks even an armed aim).
  const activateAttacker = washAim?.mode === "activate" ? state?.combatants[washAim.attackerId] : undefined;
  const activateReach = activateAttacker !== undefined && washAim !== null && activateReachForAim(activateAttacker, washAim) !== null;
  // Hover intel's zones for the rested-on unit, as this viewer may see them; `null` for a
  // non-character, which leaves the selection's wash in place.
  const intelViewer = useMemo(() => ({ role: isGm ? ("gm" as const) : ("player" as const), viewerSlot: actorId ?? null }), [isGm, actorId]);
  const hoverIntel = useMemo(
    () => (state && hoverUnitId ? hoverIntelFor({ state, hoveredId: hoverUnitId, viewer: intelViewer, ...(overlay !== undefined ? { overlay } : {}) }) : null),
    [state, hoverUnitId, intelViewer, overlay],
  );
  // The selected unit's zones (`projects/intel-follows-selection.md`), which the board shows
  // whenever no hovered unit's do: its rings beside the movement wash, and its move zone in place
  // of that wash for a unit this viewer may not move.
  const selectionIntel = useMemo(
    () => (state && selectedId && !intelOff && viewMode === "play" ? hoverIntelFor({ state, hoveredId: selectedId, viewer: intelViewer, ...(overlay !== undefined ? { overlay } : {}) }) : null),
    [state, selectedId, intelOff, viewMode, intelViewer, overlay],
  );
  const overlayMode = useMemo<OverlayMode>(
    () => (moveHover ? "movement" : overlayModeFor(washAim, selectedId, { viewMode, directionPromptActive, activateReach, hoverIntel: hoverIntel !== null })),
    [moveHover, washAim, selectedId, viewMode, directionPromptActive, activateReach, hoverIntel],
  );
  // The weapon rings, one band each (`setThreatRings`): the hovered unit's in `"intel"` mode, else
  // the selection's beside its plain movement wash. A direction prompt or a hovered Boost row owns
  // the movement mode, so neither keeps them.
  const shownIntel = useMemo(() => {
    if (overlayMode === "intel") return hoverIntel;
    if (overlayMode === "movement" && !directionPromptActive && !moveHover) return selectionIntel;
    return null;
  }, [overlayMode, hoverIntel, selectionIntel, directionPromptActive, moveHover]);
  const threatRings: ThreatRing[] | null = shownIntel?.rings ?? null;
  // What the board draws for them: each ring's cells and the cover hatch beneath (`range-preview-cover`).
  const threatRingsView = useMemo<ThreatRingsView | null>(() => (shownIntel ? { rings: shownIntel.rings.map((r) => r.keys), cover: shownIntel.cover } : null), [shownIntel]);
  // A drag's rings (`projects/intel-follows-selection.md`): the dragged unit's rings measured from
  // its would-be drop cell. The stage calls this per drag-cell change, like `pathPreview`. `null`
  // wherever the selection's rings would be off.
  //
  // **Cover-aware per cell, unless the unit's sightline pass is slow** (`range-preview-cover` F4):
  // once a pass for this unit has taken over {@link DRAG_PASS_BUDGET_MS} (in practice Size 3-4), a
  // drag gets plain rings (`settled: false`) and the stage asks again with `rest` once the pointer
  // has rested, which is always cover-aware.
  //
  // The verdict is the unit's last measured drag pass, so a rested pass that comes in under budget
  // brings per-cell rings back; before any, the selection's own pass stands in.
  const slowUnitsRef = useRef(new Map<string, boolean>());
  const threatRingsAt = useCallback(
    (combatantId: string, at: SquareCell, rest = false): DragRings | null => {
      if (!state || intelOff || viewMode !== "play" || washAim !== null || directionPromptActive) return null;
      const slowUnits = slowUnitsRef.current;
      const slow = slowUnits.get(combatantId) ?? (combatantId === selectedId && (selectionIntel?.passMs ?? 0) > DRAG_PASS_BUDGET_MS);
      const plain = !rest && slow;
      const seen = hoverIntelFor({ state, hoveredId: combatantId, viewer: intelViewer, at, plain, ...(overlay !== undefined ? { overlay } : {}) });
      if (!seen) return null;
      if (seen.passMs > 0) slowUnits.set(combatantId, seen.passMs > DRAG_PASS_BUDGET_MS);
      return { rings: seen.rings.map((r) => r.keys), cover: seen.cover, settled: !plain };
    },
    [state, intelOff, viewMode, washAim, directionPromptActive, intelViewer, overlay, selectedId, selectionIntel],
  );

  // **The state a move affordance reads** (`projects/off-turn-planning.md` 2.2): `planningStateFor`
  // for one combatant — the pre-activation preview when the room would fold one, else, off-turn and
  // for a unit this viewer may control (GM-inclusive), a hypothetical fresh turn where it stands
  // with `planning: true`; otherwise the live state with `planning: false`. `null` with no state or
  // no such combatant. The movement wash and `pathPreview` below compose against this, and it is
  // returned so the stage can tell a planning drag (`planning: true`: ghost and path only, nothing
  // dispatches) from a real one, and so the move forecast and the command menu's off-turn movement
  // previews read the same hypothetical rather than a second one. READ-ONLY: never committed, never
  // dispatched — `canDrag`'s `canPlayMove` and `dispatchMove` keep `previewIfControlled` alone.
  const moveStateFor = useCallback(
    (combatantId: string): { state: EncounterState; planning: boolean } | null => {
      const c = state?.combatants[combatantId];
      if (!state || !c) return null;
      return planningStateFor({ state, combatant: c, actorId, isGm });
    },
    [state, actorId, isGm],
  );

  // **What the reach and target washes read** (`token-command-menu` 2c.5). While an aim is armed,
  // the live state. While only a command-menu row is HOVERED, the hovered unit's `moveStateFor`
  // state: its own turn or the pre-activation preview when it has one, else (off-turn, "another
  // player is acting" or "enemy side's pick") the planning state, a fresh turn where it stands.
  // So an off-turn hover shows what that row could reach on the unit's turn, without a second
  // hypothetical. Arming stays disabled off-turn (the menu's rows are greyed), so the armed branch
  // never reads a planning state.
  const washState = useMemo<EncounterState | null>(() => {
    if (aim || !previewAim) return state;
    return moveStateFor(previewAim.attackerId)?.state ?? state;
  }, [aim, previewAim, state, moveStateFor]);

  // The movement-range overlay's reachable-cell set (the "where can this selected unit go?" wash):
  // the grid cells the selected, *placed* unit's FOOTPRINT can come to cover within its Speed, from
  // the SAME rule the server enforces (`reachableCells` over `reachableForCombatant`) — so the
  // affordance can't drift from the wall. The footprint union (not bare anchors) keeps the wash
  // aligned with the unit's body for Size > 1. Only while `overlayMode === "movement"` (which itself
  // requires PLAY + a selection — EDIT free-places with no budget — AND no armed
  // attack/tech aim, the exclusivity fix); `null` otherwise. An unplaced/speedless selection still
  // renders nothing — `reachableCells` itself returns `null` for one. Recomputed when the selection,
  // the encounter state (a move/terrain edit), or the overlay mode changes.
  //
  // **Pre-activation (2.4, D4) and off-turn planning (`off-turn-planning` 2.2):** `reachableCells`
  // is composed against `moveStateFor` (`planningStateFor`, whose first step is
  // `previewIfControlled`), NOT loosened. `reachableForCombatant`/`mayPlayMove`/`moveBudgetFor` are
  // the shared wall — read by the `move_token` reducer itself and by the bot's generator — so they
  // stay exactly as they are;
  // what changes is the STATE they are asked about. Selecting a `"ready"` unit this viewer controls
  // now paints its range off the same hypothetical post-activation state the room's own
  // `resolutionState` runs `refereeMove` against, so the wash and the wall still agree by
  // construction. Off-turn, a unit the viewer may control (GM-inclusive `canControl`) that is not
  // acting and not foldable (`on_deck`, already acted) paints a fresh turn's full-Speed reach from
  // where it stands: a plan, not a promise (`planningTurnState`). A unit the viewer may not control,
  // a destroyed unit or a non-actor gets `state` back and paints nothing; a status bar (Immobilized,
  // Stunned) still empties the planning wash, because `mayPlayMove` itself is untouched.
  //
  // **Direction-prompt branch:** while a direction prompt is active, the
  // wash paints the ENGINE'S OWN computed lunge-reachable set (`prompt.reachableCells`,
  // display-only, `shared/src/index.ts`'s `PromptRequest.reachableCells` doc) instead of the
  // ordinary selection flood — painting a naive radius here would show cells the engine would
  // later clamp away (the ray/flight-aware clamp `travelDestination` enforces server-side), which
  // is worse than the bare crosshair this replaces because it would LIE about what a click can
  // land on. A `toward:"chosen"` prompt (Siege Specialist, Ace) has no precomputed set (no anchor
  // exists yet to derive one from) — `reachableCellKeys(undefined)` is an empty set, so that shape
  // keeps its pre-existing crosshair-only affordance, unchanged.
  //
  // **Intel branch:** in `"intel"` mode the wash paints the hovered unit's move zone instead
  // (`hoverIntelFor`), `null` when this viewer may not see its Speed. A selected unit this viewer
  // may not move paints its intel move zone the same way, so a selected hostile's Speed is gated
  // as its hover is.
  const reachable = useMemo<Set<string> | null>(() => {
    if (overlayMode === "intel") return hoverIntel?.move ?? null;
    if (overlayMode !== "movement") return null;
    if (directionPromptActive) return reachableCellKeys(prompt?.reachableCells);
    if (state && moveHover) {
      const mover = state.combatants[moveHover.unitId];
      const planned = moveStateFor(moveHover.unitId);
      const after = mover && planned ? moveVerbReach({ state: planned.state, combatant: mover, verb: moveHover.verb, actorId: actorId ?? "" }) : null;
      if (after) return after;
    }
    if (!state || !selectedId) return null;
    const c = state.combatants[selectedId];
    const planned = moveStateFor(selectedId);
    if (!c || !planned) return null;
    if (selectionIntel && !isGm && !controlsCombatant(c, actorId)) return selectionIntel.move;
    return reachableCells(planned.state, c);
  }, [state, selectedId, overlayMode, directionPromptActive, prompt, moveStateFor, moveHover, actorId, hoverIntel, selectionIntel, isGm]);

  // The boost ring (v20): beside the movement wash, the cells the selected unit reaches only by
  // Boosting. Same `"movement"` gate as `reachable`, so a hover preview or an armed aim (both of
  // which outrank movement in `overlayModeFor`) hides it; empty whenever the reducer would refuse
  // the Boost. `boostRingCells` composes the real `boost` reducer over the same preview.
  const boostRange = useMemo<Set<string> | null>(() => {
    if (overlayMode !== "movement" || directionPromptActive || moveHover || !state || !selectedId) return null;
    const c = state.combatants[selectedId];
    return c ? boostRingCells({ state, combatant: c, actorId, isGm }) : null;
  }, [state, selectedId, overlayMode, directionPromptActive, moveHover, actorId, isGm]);

  // The Sensors (TECH-RANGE) reach wash: gated on
  // `overlayMode === "tech"` — ANY Sensors-scoped aim (the shipped `search` mode, the `quick_tech`/
  // `full_tech` aim modes, and — a system-granted
  // `tech_attack`) now EXCLUSIVE of the move/attack washes.
  // `overlayModeFor` (`combatCells.ts`) is what actually decides "tech" vs "movement"/"attack" for
  // a given aim — this memo just reads its verdict, so getting a NEW Sensors-scoped mode wrong
  // lives (and is pinned) there, not here. The gate + geometry live in `sensorReachCells`
  // (`combatInspector.ts`) so they unit-test without the component.
  const sensorReach = useMemo<Set<string> | null>(() => {
    if (!washState || overlayMode !== "tech") return null;
    return sensorReachCells(washState, washAim);
  }, [washState, washAim, overlayMode]);

  // The live move-path preview (the "tiles this drag will travel through" highlight): given the
  // dragged combatant and the cell its anchor currently hovers, the footprint cells the cheapest
  // legal path sweeps — the SAME `move_token` rule the server enforces (`movePathPreview`), so the
  // trail can never disagree with what a drop will accept. A drop only a Boost reaches folds that
  // Boost (`move-auto-boost`), so its trail is routed on the boosted budget and the part past the
  // move left comes back as `boostCells`. Only in PLAY mode (EDIT free-places with
  // no budget/path); `null` for an out-of-range / illegal drop (nothing commits).
  // The stage calls this per drag-cell-change and renders the returned set under the token layer.
  //
  // **Pre-activation (2.4) and off-turn planning (2.2):** composed against `moveStateFor` for the
  // same reason the `reachable` wash above is — `movePathPreview` opens with the very same
  // `mayPlayMove` wall, so a pre-activation drag would otherwise stream a `null` trail while the
  // drop it is heading for is one the room will accept and fold an activation in front of.
  //
  // **The drag gesture's halves read two states, on purpose.** What a drag SHOWS (this path and
  // the `reachable` wash) reads the planning state; what a drag may COMMIT reads the commit state,
  // `previewIfControlled` alone: `canDrag` (`CombatStage`, via `CombatApp`'s `canPlayMove`) and
  // `dispatchMove` (`remoteEncounterController`). On a unit's own turn, or pre-activation, the two
  // states are the same object (`planning: false`), so all three halves still agree. Off-turn this
  // path answers for a unit `canDrag` refuses; the only way to reach it off-turn is a planning drag
  // (`moveStateFor(id).planning`, 2.3), which must never dispatch.
  const pathPreview = useCallback(
    (combatantId: string, dest: SquareCell): MovePathPreview | null => {
      if (viewMode !== "play") return null;
      const c = state?.combatants[combatantId];
      const planned = moveStateFor(combatantId);
      if (!c || !planned) return null;
      return movePathPreview(planned.state, c, dest);
    },
    [state, viewMode, moveStateFor],
  );

  // A Trampler's drag trail (`landing-zone-full-automation` Q3): the trail after the pointer enters
  // `dest` (`trampleTrailTo`, over the same `moveStateFor` state as `pathPreview`) and the footprint
  // cells it sweeps. `reached: false` keeps the old trail when `dest` can't be reached from its end.
  // `null` for any other mover, which keeps `pathPreview`'s engine route. PLAY only.
  const trampleTrail = useCallback(
    (combatantId: string, trail: readonly Cell[], dest: SquareCell): TrampleTrail | null => {
      if (viewMode !== "play") return null;
      const planned = moveStateFor(combatantId);
      const c = planned?.state.combatants[combatantId];
      if (!planned || !c || !hasStatus(c.statuses, TRAMPLING_STATUS)) return null;
      const next = trampleTrailTo(planned.state, c, { trail, dest });
      const walked = next ?? (trail.length > 0 ? [...trail] : c.pos != null ? [c.pos] : []);
      const world = worldFor(planned.state);
      const cells = new Set<string>();
      for (const anchor of walked) for (const cell of world.footprint(anchor, c.base.size).cells()) cells.add(world.key(cell));
      return { trail: walked, cells, reached: next !== null };
    },
    [viewMode, moveStateFor],
  );

  // The move forecast (off-turn-planning 3.1): what committing a drag to `dest` would provoke, over
  // the SAME `moveStateFor` state `pathPreview` reads, so the callout's list and the trail agree
  // (`forecastMove` is `null` exactly where the path is: an illegal drop). PLAY only, like the path.
  // A Trampler's drag passes its trail as `path`, forecast along it (`null` over budget).
  // The wire carries full stat blocks, so the forecast is cut to this viewer (`moveForecastFor`):
  // the "could reach you next turn" row never names a hostile whose Speed and reach are masked.
  const moveForecast = useCallback(
    (combatantId: string, dest: SquareCell, path?: readonly Cell[]): MoveForecast | null => {
      if (viewMode !== "play") return null;
      const planned = moveStateFor(combatantId);
      if (!planned) return null;
      return moveForecastFor(forecastMove(planned.state, { moverId: combatantId, to: dest, ...(path !== undefined ? { path } : {}) }), {
        state: planned.state,
        viewerSlot: actorId ?? null,
        role: isGm ? "gm" : "player",
      });
    },
    [viewMode, moveStateFor, actorId, isGm],
  );

  // The attack-range overlay's reach wash (the "where can this armed weapon hit?" red field), as
  // actual cells: the cells the armed Skirmish weapon can target, from the frozen `WeaponProfile.range`
  // (`attackReachCellList`). Only while an attack is armed in PLAY; `null` otherwise. Kept as cells
  // (not just keys) so `cellSightlines` below can run a per-cell LoS query against each without
  // re-parsing string keys.
  const attackReachList = useMemo(() => {
    if (!washState || overlayMode !== "attack" || !washAim) return null;
    const attacker = washState.combatants[washAim.attackerId];
    if (!attacker) return null;
    // A melee verb (Ram, Grapple, Improvised Attack) washes the threat-1 reach its target mirror
    // tests adjacency against (`meleeReachForAim`), armed or hovered.
    // A granted single-target action washes its frozen reach (`activateReachForAim`).
    const range = weaponRangeForAim(attacker, washAim, washState.activation.turnOrdinal) ?? meleeReachForAim(washAim) ?? activateReachForAim(attacker, washAim);
    if (!range) return null;
    return attackReachCellList(washState, attacker, range);
  }, [washState, washAim, overlayMode]);

  // The directed-deploy **legal-cell wash** — a
  // FUNDAMENTALLY DIFFERENT derivation than `attackReachList` above: `weaponRangeForAim` stays `null`
  // for `activate_sites` on purpose (Jericho has no `actionRanges` entry, so bolting the wash
  // onto that seam is wrong even where it happens to return something), so this reads
  // `legalDirectedDeployCells` directly off live state + the draft's own accumulated `picks`, and
  // RECOMPUTES on every pick (the memo's `[state, aim, overlayMode]` deps include `aim`, which is a
  // fresh object each `appendDirectedDeployPick` call). `remaining` (D6's lookahead knob) is derived
  // from the draft the SAME way `checkDirectedDeployPlacement` derives it server-side — `count -
  // picks.length - 1` for the pick about to be enumerated — so the two can't independently disagree
  // about how much lookahead applies. `null` outside `overlayMode === "attack"` (this mode only
  // resolves there once armed — see `combatCells.ts`'s `ATTACK_OVERLAY_AIM_MODES`) or when nothing's
  // armed/it's a different mode.
  const directedDeployWash = useMemo<Set<string> | null>(() => {
    if (!state || overlayMode !== "attack" || !aim || aim.mode !== "activate_sites") return null;
    const world = worldFor(state);
    const remaining = aim.count - aim.picks.length - 1;
    // `aim.mine` (threaded from `armDirectedDeployDraft`) folds
    // in `checkMineAdjacency`'s "not adjacent to any other mines" wall for a `deploy_mine` draft —
    // absent for every non-mine directed deploy (Jericho, the turret), so this call stays
    // byte-identical to its pre-D6 shape for them.
    const legal = legalDirectedDeployCells({
      state,
      deployerId: aim.attackerId,
      size: aim.size,
      placed: aim.picks,
      remaining,
      ...(aim.mine ? { mine: true } : {}),
      ...(aim.placement ? { placement: aim.placement } : {}),
    });
    return new Set(legal.map((c) => world.key(c)));
  }, [state, aim, overlayMode]);

  // The Dismount/Eject **landing-cell wash** — another
  // derivation `weaponRangeForAim` doesn't cover (neither verb has a weapon or a granted-action
  // range), so this reads `legalPilotBridgeCells` (`attackRange.ts`) directly, mirroring
  // `directedDeployWash`'s own "a fundamentally different wash gets its own memo" shape. Gated on
  // `overlayMode === "attack"` exactly like that memo (this mode only resolves there once armed —
  // `combatCells.ts`'s `ATTACK_OVERLAY_AIM_MODES` now carries `dismount`/`eject`).
  // It reads the wash aim, so a hovered Dismount / Eject menu row previews its landing spaces
  // (`token-command-menu` 3.2), over the hovered unit's own `washState` off-turn.
  const pilotBridgeWash = useMemo<Set<string> | null>(() => {
    if (!washState || overlayMode !== "attack" || !washAim || (washAim.mode !== "dismount" && washAim.mode !== "eject")) return null;
    return legalPilotBridgeCells({ state: washState, mechId: washAim.attackerId, mode: washAim.mode });
  }, [washState, washAim, overlayMode]);

  // The Dismount (ally) / `ride_dismount` landing-cell wash — {@link pilotBridgeWash}'s sibling
  // for the riding rider's own explicit climb-off:
  // `legalRideDismountCells` (`attackRange.ts`) reads the carrier off the RIDER's own `Riding`
  // marker server-side (no `mechId` to pass here, unlike `pilotBridgeWash` above), so this is its
  // own separate memo rather than folded into that one's branch.
  const rideDismountWash = useMemo<Set<string> | null>(() => {
    if (!state || overlayMode !== "attack" || !aim || aim.mode !== "ride_dismount") return null;
    return legalRideDismountCells({ state, riderId: aim.attackerId });
  }, [state, aim, overlayMode]);

  // The cells ALREADY picked in a live directed multi-pick placement —
  // what the stage paints a translucent section mark on, so each section appears as its cell is
  // committed instead of both appearing at once when the final pick fires.
  //
  // The **which-cells** rule lives in `directedDeployPendingMarks` (`combatInspector.ts`, unit-tested
  // beside the pick transitions it reads); this memo only maps its cells to the render layer's string
  // keys. Keeping the rule out of the component is what makes it pinnable at all — the marks
  // themselves are canvas nodes with no DOM to assert on.
  //
  // NOT gated on `overlayMode === "attack"` the way `directedDeployWash` is: the wash rides the
  // shared attack-overlay rendering seam (which needs that mode to even compute), but these marks are
  // their own independent layer, and a pick the player has already committed should stay visible
  // regardless of which overlay is showing.
  const pendingDeploy = useMemo<{ cells: string[]; size: number } | null>(() => {
    const marks = directedDeployPendingMarks(aim);
    if (!state || !marks) return null;
    const world = worldFor(state);
    return { cells: marks.cells.map((c) => world.key(c)), size: marks.size };
  }, [state, aim]);

  // The bare-key twin of `attackReachList` — **a real boundary**: mirrors
  // the server's `checkAttackRange` gate — an out-of-range target is un-targetable (see
  // `outOfRangeTargetIds` below), so the player can't even attempt the shot the authority would reject.
  // **Also carries the directed-deploy wash** when `activate_sites` is armed — the two modes
  // never coexist (one `aim` at a time), so `directedDeployWash` standing in here is the "give
  // `attackRange` a separate branch" this mode needs, and it's what makes `cellPickBypassesSelect`,
  // `onCommitCell`'s illegal-cell guard, and `outOfRangeTargetIds`'s reach check all correctly
  // read the LEGAL-CELL set instead of `null` (⇒ "ungated") the moment this mode is armed.
  const attackRange = useMemo<Set<string> | null>(() => {
    if (directedDeployWash) return directedDeployWash;
    if (pilotBridgeWash) return pilotBridgeWash;
    if (rideDismountWash) return rideDismountWash;
    return attackReachList ? new Set(attackReachList.map((r) => r.key)) : null;
  }, [attackReachList, directedDeployWash, pilotBridgeWash, rideDismountWash]);

  // The hovered attack-affected template (the red AoE preview): given the cell under the cursor, the
  // cells a committed shot would hit — the target plus its Blast/Line/Cone area (`attackAffectedCells`).
  // The attack twin of `pathPreview`, but driven by free hover rather than a drag. `null` when no attack
  // is armed. The stage calls this per hovered-cell-change and emphasizes the returned set in brighter red.
  const attackAffected = useCallback(
    (hovered: SquareCell): Set<string> | null => {
      if (!state || overlayMode !== "attack" || !aim) return null;
      const attacker = state.combatants[aim.attackerId];
      if (!attacker) return null;
      const range = weaponRangeForAim(attacker, aim, state.activation.turnOrdinal);
      if (!range) return null;
      // The hovered cell is both the Blast/Burst center and the Line/Cone aim (`toward` defaults to it,
      // matching the resolver's cell-as-toward fallback). Grid-agnostic via the `worldFor(state)` port.
      return attackAffectedKeys({ state, attacker, aim: hovered, range });
    },
    [state, aim, overlayMode],
  );

  // The hovered AoE template's **LoS-excluded** cells (the UI
  // preview-parity twin of the engine's own fan-out filter): the footprint cells of every combatant
  // `attackAffected` above catches geometrically but the measuring center can't SEE (same D4 anchor +
  // terrainVisibility + tg_seeking/tg_arcing exemption `attackAffectedCombatantKeys` reads). RAW never
  // shrinks the painted template itself — only WHO it resolves against — so this is a SEPARATE overlay
  // from `attackAffected`, not a smaller version of it; the stage renders both (the red template, plus
  // a muted marker over any sheltered token inside it) so the GM sees the true hit set before
  // committing. `null` (no markers) for a non-area weapon — LoS there is the initiation gate's job.
  //
  // **Cell-pick throws (`activate_cell`) are deliberately excluded**: this overlay's whole point
  // is preview PARITY with what the resolver actually
  // drops, but the site-based `for_each` a thrown Smoke/HEX Charge resolves through
  // (`interpretForEach`/`enumerateArea`, `packages/rules/src/encounter/effects/`) applies NO LoS
  // filter at all — `resolveActivate`'s own comment records this as deliberate ("Range and LoS
  // are not gated here", `packages/rules/src/encounter/resolve/activate.ts`). Painting
  // a "sheltered, spared" marker here would tell the player an ally behind cover is safe from the
  // blast when the resolver will hit them anyway — the "template shows less than what's hit" drift in
  // reverse. Suppressed until the engine actually gates site-origin `for_each` on LoS.
  // `activate_sites` (directed multi-pick placement)
  // USED to reach this same "no veil" outcome BY ACCIDENT — `combatCells.ts`'s
  // `ATTACK_OVERLAY_AIM_MODES` didn't include it, so `overlayMode` could never resolve to `"attack"`
  // while it was armed, and the `overlayMode !== "attack"` guard below returned `null` first with no
  // separate check needed. `activate_sites` joined that Set (the legal-cell wash needs `"attack"` to
  // paint through `cellSightlines`), which removed that accidental protection — so this carries
  // the SAME EXPLICIT exclusion `activate_cell` has, via the shared
  // `LOS_VEIL_EXCLUDED_AIM_MODES` set (`combatCells.ts`): the directed-deploy resolution path
  // (`interpretDeployObject`) applies no LoS filter at all, exactly like the point-blanket throw case
  // above, so painting a "sheltered, spared" marker here would be the identical lie.
  const attackLosExcluded = useCallback(
    (hovered: SquareCell): Set<string> | null => {
      if (!state || overlayMode !== "attack" || !aim || LOS_VEIL_EXCLUDED_AIM_MODES.has(aim.mode)) return null;
      const attacker = state.combatants[aim.attackerId];
      if (!attacker) return null;
      const range = weaponRangeForAim(attacker, aim, state.activation.turnOrdinal);
      if (!range || !isAreaRange(range)) return null;
      const weapon = weaponProfileForAim(attacker, aim);
      const cells = attackAffectedKeys({ state, attacker, aim: hovered, range });
      const projected: ProjectedEncounterState = state;
      const sparesAllies = weapon !== null && (projected.sparingWeapons?.[attacker.id] ?? []).includes(weaponInstanceKey(weapon));
      const trueHit = new Set(attackAffectedCombatantKeys({ state, attacker, aim: hovered, range, tags: weapon?.tags, sparesAllies }));
      const keys = new Set<string>();
      for (const [id, c] of Object.entries(state.combatants)) {
        if (id === attacker.id || trueHit.has(id)) continue;
        if (combatantInReachKeys(state, c, cells)) {
          for (const k of combatantFootprintKeys(state, c)) keys.add(k);
        }
      }
      return keys.size > 0 ? keys : null;
    },
    [state, aim, overlayMode],
  );

  // Whether the armed Skirmish weapon projects an AREA template (Blast/Line/Cone/Burst). When true,
  // the stage lets a bare-cell click commit the attack at that cell (the AoE origin) via `onCommitCell`;
  // single-target weapons keep the click-a-unit-to-fire gesture. `false` when no attack is armed.
  const attackArea = useMemo<boolean>(() => {
    if (!state || viewMode !== "play" || !aim) return false;
    // A cell-pick deploy/throw (`activate_cell` — Smoke/HEX Charges to a point,
    // throwable deployables): the next bare-cell click commits the `site`, exactly like an
    // AoE origin. It rides this same "a bare-cell click commits" flag (and `onCommitCell` below).
    if (aim.mode === "activate_cell") return true;
    // A directed multi-pick placement (`activate_sites` — Jericho's two sections, the turret's one,
    // `activate_sites`): every pick is a bare-cell (or legally-bypassed
    // occupied-cell) commit too, exactly like `activate_cell` above — see `onCommitCell`'s
    // `activate_sites` branch for what actually happens on the click (append a pick, or fire once the
    // draft completes).
    if (aim.mode === "activate_sites") return true;
    // Dismount/Eject are the SAME "next bare/occupied-cell
    // click commits" gesture — the pilot's landing space, not a token-fire target.
    if (aim.mode === "dismount" || aim.mode === "eject") return true;
    // Dismount (ally) / `ride_dismount` joins the same
    // gesture — the rider's own landing space, never a token-fire target.
    if (aim.mode === "ride_dismount") return true;
    if (aim.mode !== "skirmish" && aim.mode !== "barrage") return false;
    const attacker = state.combatants[aim.attackerId];
    if (!attacker) return false;
    const range = weaponRangeForAim(attacker, aim, state.activation.turnOrdinal);
    return range ? isAreaRange(range) : false;
  }, [state, aim, viewMode]);

  // Whether a **site pick** is armed — `activate_cell` (Smoke/HEX Charges) OR `activate_sites`
  // (directed multi-pick placement). `attackArea` above is already
  // true for both modes (they ride the same "a bare-cell click commits" flag as an area Skirmish/
  // Barrage weapon), but the STAGE needs the narrower fact: unlike an area weapon (where clicking a
  // token still fires the weapon directly AT that unit via `aimResolvesToFire`/`fireAt` — a distinct,
  // valid gesture from clicking its cell), NEITHER cell-pick mode has a "fire at this unit" concept at
  // all — every commit is a site, token-occupied or not (RAW puts no restriction on a Blast center OR
  // a Jericho section; a smoke grenade can land square on an ally, and a cover section can land on the
  // deployer's own adjacent occupied cell same as an empty one). `aimResolvesToFire` returns `false`
  // for BOTH modes for exactly this reason (see its own doc). So while EITHER is armed, the stage must
  // not let a token PRESS claim the click for select/drag at all — it has to fall through to the same
  // bare-cell `onCommitCell` commit an empty cell already reaches. See `CombatStage`'s `cellPickArmed`
  // prop, and D5 (`cellPickBypassesSelect`'s existing predicate — reused unchanged; `attackRange`
  // itself is what differs per mode, folding in `directedDeployWash` above for `activate_sites`).
  // **One flag covering two modes is deliberate, not a conflation**: the property this flag names
  // ("every commit is a site, never a token-fire") is genuinely the SAME property for both — a
  // separate `sitesPickArmed` prop would duplicate the identical routing logic in `CombatStage.tsx`
  // for no behavioral difference, since the two modes never coexist (one `aim` at a time) and
  // `onCommitCell`/`attackRange` already fully disambiguate what happens once the click lands.
  // `dismount`/`eject` join too — neither has a "fire at this
  // unit" concept (see `aimResolvesToFire`'s own explicit exclusion), so a token press while either is
  // armed must fall through to the same bare-cell commit an empty cell already reaches.
  const cellPickArmed =
    viewMode === "play" &&
    (aim?.mode === "activate_cell" ||
      aim?.mode === "activate_sites" ||
      aim?.mode === "dismount" ||
      aim?.mode === "eject" ||
      aim?.mode === "ride_dismount");

  // Whether the board is in **target-picking mode** — a Skirmish, Barrage, Ram, Grapple, Quick Tech,
  // Full Tech, or Release aim is live in PLAY. Drives the stage's crosshair cursor (the affordance
  // that the surface is now "click a target", not select). Ram, Grapple, and Improvised Attack are
  // all single-target melee picks on the board (Ram's knock-back is pre-declared in-panel;
  // Grapple/Improvised Attack have no up-front declaration at all). **Quick Tech**
  // joins the same list as Barrage: the crosshair is live the moment
  // Quick Tech is armed, even before an option row is picked (mirrors Barrage's own gate — only
  // `aimResolvesToFire` waits for `weaponId`/`optionId` to be non-empty before an actual click fires).
  // **Full Tech** mirrors Quick Tech's gate exactly, one pick at a
  // time. **A system-granted tech attack** (`tech_attack`)
  // joins the same list — its `actionId` is already named at arm time (the granted row itself), so
  // like Barrage/Quick Tech the crosshair is live from the moment it's armed. **Release**
  // is the same shape as Grapple/Improvised Attack —
  // no up-front declaration, the next token click fires it.
  const targetPicking =
    viewMode === "play" &&
    aim !== null &&
    (aim.mode === "skirmish" ||
      aim.mode === "barrage" ||
      aim.mode === "ram" ||
      aim.mode === "grapple" ||
      aim.mode === "improvised_attack" ||
      aim.mode === "quick_tech" ||
      aim.mode === "full_tech" ||
      // A system-granted TECH ATTACK (`tech_attack`) joins
      // Quick Tech/Full Tech's exact gate — the crosshair is live the moment the granted row arms
      // it (there's no in-panel option pick to wait out; `actionId` is already named at arm time).
      aim.mode === "tech_attack" ||
      aim.mode === "activate_cell" ||
      // Directed multi-pick placement (`activate_sites`) —
      // the crosshair reads "click a cell", same as `activate_cell`'s single-pick throw; free, per the
      // tracker's own framing (this list already carries `activate_cell`).
      aim.mode === "activate_sites" ||
      // Dismount/Eject — the crosshair reads "click a cell",
      // same as `activate_cell`'s single-pick throw.
      aim.mode === "dismount" ||
      aim.mode === "eject" ||
      // Dismount (ally) / `ride_dismount` joins the same
      // "click a cell" crosshair.
      aim.mode === "ride_dismount" ||
      aim.mode === "release");

  // The non-attacker combatants the armed Skirmish weapon can NOT directly target — the UI twin of
  // the server's per-attack legality walls. Two reasons land a unit here:
  //   • OUT OF REACH — its footprint the reach wash doesn't touch (`combatantInReach`, the UI twin
  //     of `checkAttackRange`); only when the weapon HAS a usable reach (`attackRange` non-null —
  //     a reach-unknown fallback strike / no-Sensors weapon gates nothing on range, mirroring the
  //     server's reach-unknown carve-out).
  //   • HIDDEN — a HIDDEN combatant can't be tapped as a direct-attack target,
  //     the affordance for the server's Hidden room gate; gated regardless of reach (a Blast at a
  //     cell can still catch it, but a unit-tapped direct shot can't). Mirrors the global Hidden flag.
  // Drives the hard-disable: these tokens render dimmed/un-targetable and
  // a click on one never fires (it falls through to plain selection). `null` only when nothing is armed.
  // **Ram (melee-control-actions) gates here too**: when Ram is armed the un-targetable set is every
  // combatant the Ram legality mirror (`ramTargetReason` — adjacency + the Size rule) rejects, so only a
  // valid adjacent target fires on click; an illegal one dims and falls through to select.
  // **Grapple mirrors the same shape**, minus the Size rule — adjacency only
  // (`grappleTargetReason`). **Improvised Attack (RAW Core p.71) mirrors
  // the identical shape as Grapple** — adjacency only (`improvisedAttackTargetReason`), no Size rule.
  const outOfRangeTargetIds = useMemo<Set<string> | null>(() => {
    if (!washState || !washAim) return null;
    const attacker = washState.combatants[washAim.attackerId];
    if (washAim.mode === "ram") {
      if (!attacker) return null;
      const ids = new Set<string>();
      for (const [id, c] of Object.entries(washState.combatants)) {
        if (id === washAim.attackerId) continue;
        if (ramTargetReason(washState, attacker, c) !== null) ids.add(id);
      }
      return ids;
    }
    if (washAim.mode === "grapple") {
      if (!attacker) return null;
      const ids = new Set<string>();
      for (const [id, c] of Object.entries(washState.combatants)) {
        if (id === washAim.attackerId) continue;
        if (grappleTargetReason(washState, attacker, c) !== null) ids.add(id);
      }
      return ids;
    }
    if (washAim.mode === "improvised_attack") {
      if (!attacker) return null;
      const ids = new Set<string>();
      for (const [id, c] of Object.entries(washState.combatants)) {
        if (id === washAim.attackerId) continue;
        if (improvisedAttackTargetReason(washState, attacker, c) !== null) ids.add(id);
      }
      return ids;
    }
    // Quick Tech AND Full Tech (same `optionId`/`accuracy`
    // washAim shape, one pick at a time) — mirrors Ram/Grapple's shape: every combatant `techTargetReason`
    // rejects for the ARMED option (Sensors range, tech-immunity, self, the willing-ally-Invade
    // deferral) dims and falls through to plain select on click. Before an option row is armed
    // (`optionId === ""`) there's nothing to gate yet — everyone stays targetable so the Sensors
    // overlay/crosshair don't dim the board while the player is still picking an option.
    if (washAim.mode === "quick_tech" || washAim.mode === "full_tech") {
      if (!attacker || washAim.optionId === "") return null;
      const ids = new Set<string>();
      for (const [id, c] of Object.entries(washState.combatants)) {
        if (id === washAim.attackerId) continue;
        if (techTargetReason({ state: washState, attacker, optionId: washAim.optionId, target: c }) !== null) ids.add(id);
      }
      return ids;
    }
    // A pilot's Mount, Jockey and Ride — every unit the chooser card would not offer (its own
    // legality mirror) dims and falls through to plain select on click.
    if (washAim.mode === "mount" || washAim.mode === "jockey" || washAim.mode === "ride") {
      if (!attacker) return null;
      const reason = washAim.mode === "mount" ? mountTargetReason : washAim.mode === "jockey" ? jockeyTargetReason : rideTargetReason;
      const ids = new Set<string>();
      for (const [id, c] of Object.entries(washState.combatants)) {
        if (id === washAim.attackerId) continue;
        if (reason(washState, attacker, c) !== null) ids.add(id);
      }
      return ids;
    }
    // Search — every combatant the Search card would not offer (not Hidden, or outside Sensors)
    // dims and falls through to plain select on click, the same `searchTargetReason` filter.
    if (washAim.mode === "search") {
      if (!attacker) return null;
      const ids = new Set<string>();
      for (const [id, c] of Object.entries(washState.combatants)) {
        if (id === washAim.attackerId) continue;
        if (searchTargetReason(washState, attacker, c) !== null) ids.add(id);
      }
      return ids;
    }
    // System-granted tech attack — same `techTargetReason`
    // wall Quick/Full Tech use above, keyed on the row's composite `actionId` (a `<itemId>:
    // <actionIndex>` id, not one of `quickTechOption`'s four) instead of an `optionId`.
    // `techTargetReason` falls back to treating an unrecognized id as the tech-attack action id
    // itself (see its doc comment) so Sensors range + tech-immunity + the same-side deferral all
    // still gate a Markerlight-style row exactly like Invade does. No "unarmed" state to wait out —
    // the row click already named `actionId`, so this gates from the moment it's armed.
    if (washAim.mode === "tech_attack") {
      if (!attacker) return null;
      const ids = new Set<string>();
      for (const [id, c] of Object.entries(washState.combatants)) {
        if (id === washAim.attackerId) continue;
        if (techTargetReason({ state: washState, attacker, optionId: washAim.actionId, target: c }) !== null) ids.add(id);
      }
      return ids;
    }
    // Skirmish AND Barrage gate the same way — both key the reach wash to the armed weapon
    // (`barrage-sequential-resolution`: the per-shot range gate dims a target the armed Barrage weapon
    // can't reach). `attackRange === null` (no weapon armed yet) leaves everyone targetable. This
    // branch is the token-dimming twin of the `attack` cell-dot overlays above, so it's ALSO gated on
    // `overlayMode === "attack"` — in practice a no-op today
    // (`overlayMode` resolves to `"attack"` exactly when a Skirmish/Barrage washAim is armed, since `washAim`
    // only ever lives while `viewMode === "play"`), stated explicitly so this branch can't silently
    // desync from the reach-wash dots it mirrors if the precedence ever changes. **Deliberately NOT**
    // applied to the Ram/Grapple/Improvised Attack/Quick Tech/Full Tech branches above: those modes
    // carry no cell-dot vocabulary of their own (`overlayModeFor` leaves them un-mapped, falling
    // through to `"movement"`/`"none"`), so gating the whole memo on `"attack"` would silently drop
    // their untargetable-token dimming — a functional regression, not an exclusivity fix.
    if (washAim.mode !== "skirmish" && washAim.mode !== "barrage") return null;
    if (overlayMode !== "attack") return null;
    if (!attacker) return null;
    // The armed weapon's resolved profile (`weaponProfileForAim`, `attackRange.ts`) — resolved ONCE
    // here, not re-derived per candidate, and reused below for the LoS-exemption check
    // (`tg_seeking`/`tg_arcing`) exactly as `checkAttackLoS`/`checkAttackCellLoS` read it server-side.
    const weapon = weaponProfileForAim(attacker, washAim);
    const ids = new Set<string>();
    // The per-target rule (out of reach, Hidden, or no line of sight; a weapon with no usable reach
    // stands down both the range and LoS walls, as `checkAttackLoS` does) is `weaponTargetBlocked`
    // (`attackRange.ts`), shared with the command menu's "N in reach" count (`targetsInReach`).
    for (const [id, c] of Object.entries(washState.combatants)) {
      if (id === washAim.attackerId) continue;
      if (weaponTargetBlocked({ state: washState, attacker, target: c, weapon, reach: attackRange })) ids.add(id);
    }
    return ids;
  }, [washState, attackRange, washAim, overlayMode]);

  // The on-canvas **per-cell targeting advisory** (`los-per-cell-visualization`): while a
  // Skirmish/Barrage is armed, one {@link CellSightline} per reach-wash cell — targetable (LoS-clear)
  // + cover degree, computed cell-by-cell via `armedCellSightlines` (`combatInspector.ts`), not per
  // occupying combatant. `TargetableCellLayer` only dots a cell when it's targetable, and rings the
  // ones under cover. `null` when nothing is armed; the reach-list gate (`attackReachList`) already
  // limits it to skirmish/barrage in PLAY.
  //
  // **`activate_sites` (directed multi-pick placement) takes a SEPARATE branch**
  // that skips `armedCellSightlines`/`terrainVisibility` entirely — RAW's placement constraint (free +
  // adjacent-to-mech + adjacent-to-the-last-section) has no LoS/cover component at all, so running the
  // reach wash's LoS query here would be both wrong (a legal-but-walled-off cell would wrongly read
  // `visible: false` and drop out of the painted wash) and redundant (`directedDeployWash` above
  // already IS the legal-cell set — nothing left to filter). Every cell in `directedDeployWash` maps to
  // a plain `{visible: true, cover: "none"}` so `pixiCombatBoard.ts`'s `setTargeting` — the SAME
  // rendering call the reach wash paints through — draws the SAME red dot for it, reusing the existing
  // overlay-cell painting machinery per D4's own framing ("only the derivation is new").
  const cellSightlines = useMemo<CellSightline[] | null>(() => {
    if (!washState || overlayMode !== "attack" || !washAim) return null;
    if (washAim.mode === "activate_sites") {
      if (!directedDeployWash) return null;
      return Array.from(directedDeployWash, (key) => ({ key, visible: true, cover: "none" as const }));
    }
    // Dismount/Eject take the SAME separate branch as
    // `activate_sites` above, for the identical reason: RAW's bridge placement constraint
    // (adjacency/fly-6 + a free landing space) has no LoS/cover component, so `pilotBridgeWash`
    // (already the legal-cell set) maps straight to `{visible: true, cover: "none"}` rather than
    // running it back through `armedCellSightlines`'s LoS query.
    if (washAim.mode === "dismount" || washAim.mode === "eject") {
      if (!pilotBridgeWash) return null;
      return Array.from(pilotBridgeWash, (key) => ({ key, visible: true, cover: "none" as const }));
    }
    // Dismount (ally) / `ride_dismount` takes the SAME
    // separate branch, for the identical reason — RAW's adjacent-free-space gate has no LoS/cover
    // component either.
    if (washAim.mode === "ride_dismount") {
      if (!rideDismountWash) return null;
      return Array.from(rideDismountWash, (key) => ({ key, visible: true, cover: "none" as const }));
    }
    const attacker = washState.combatants[washAim.attackerId];
    if (!attacker) return null;
    // The armed weapon is threaded in for the SAME two RAW tag exemptions the server's
    // `checkAttackCellLoS` applies (`tg_arcing`/`tg_seeking` both ignore line of sight; Seeking also
    // ignores cover) — resolved off the SAME `weaponProfileForAim` seam `outOfRangeTargetIds` above
    // reads for its per-combatant LoS twin, so the cell wash and the token dimming can't disagree
    // about which weapon is armed. Without it an ARCING mortar's pips vanish behind every wall it is
    // specifically designed to lob over. A melee verb (Ram, Grapple) passes its bare Threat reach so
    // `coverAppliesTo` drops the hatch, as it does for a melee weapon.
    const meleeVerb = meleeReachForAim(washAim);
    return armedCellSightlines({
      state: washState,
      attacker,
      reachCells: attackReachList,
      weapon: weaponProfileForAim(attacker, washAim) ?? (meleeVerb ? { range: meleeVerb } : null),
      ...(overlay !== undefined ? { overlay } : {}),
    });
  }, [washState, washAim, attackReachList, overlayMode, directedDeployWash, pilotBridgeWash, rideDismountWash, overlay]);

  // The units the washed action could legally target — the complement of `outOfRangeTargetIds`
  // (the SAME per-mode walls), narrowed to units on the side the action is for: allies for
  // Bolster, hostiles otherwise. `null` when the mode has no target mirror.
  const legalTargetIds = useMemo<string[] | null>(() => {
    if (!washState || !washAim || !outOfRangeTargetIds) return null;
    const attacker = washState.combatants[washAim.attackerId];
    if (!attacker) return null;
    const allies = ((washAim.mode === "quick_tech" || washAim.mode === "full_tech") && washAim.optionId === "bolster") || washAim.mode === "mount" || washAim.mode === "ride";
    return Object.values(washState.combatants)
      .filter((c) => c.id !== attacker.id && isActor(c) && c.pos != null && !outOfRangeTargetIds.has(c.id))
      .filter((c) => (sideOf(c) === sideOf(attacker)) === allies)
      .map((c) => c.id);
  }, [washState, washAim, outOfRangeTargetIds]);

  return {
    overlayMode,
    legalTargetIds,
    boostRange,
    reachable,
    threatRings,
    threatRingsView,
    threatRingsAt,
    sensorReach,
    pathPreview,
    trampleTrail,
    moveForecast,
    moveStateFor,
    attackReachList,
    directedDeployWash,
    pilotBridgeWash,
    rideDismountWash,
    pendingDeploy,
    attackRange,
    attackAffected,
    attackLosExcluded,
    attackArea,
    cellPickArmed,
    targetPicking,
    outOfRangeTargetIds,
    cellSightlines,
  };
}

/**
 * The longest a drag may spend on one cover-aware ring pass per cell (`range-preview-cover` F4): one
 * 60 Hz frame. A unit whose pass runs longer drags with plain rings and snaps to cover-aware at rest.
 */
export const DRAG_PASS_BUDGET_MS = 16;

